import { api } from "@/lib/api";
import { subscribePush } from "@/lib/pwa";

/**
 * Notificações push — ativas por defeito.
 *
 * O browser não deixa a app ativá-las sozinha: a permissão do sistema tem de
 * ser aceite pela pessoa, e o Safari só a pede dentro de um gesto. Por isso a
 * ativação automática (`useAutoPush`) faz duas coisas:
 *   - permissão já concedida → subscreve em silêncio (também reassocia o
 *     dispositivo à conta depois de um logout/login);
 *   - permissão ainda por decidir → pede-a no primeiro toque na app.
 * Permissão recusada no sistema não se volta a pedir — só a pessoa a muda.
 *
 * Só se guarda a recusa feita na app ("Desativar" no Perfil), por dispositivo.
 * Enquanto existir, nada é pedido nem subscrito automaticamente.
 */

const OPT_OUT_KEY = "taskflow.push.optout.v1";
const ACTIVATION_TIMEOUT_MS = 20000;

export function isPushOptedOut() {
  try {
    return localStorage.getItem(OPT_OUT_KEY) === "1";
  } catch (e) {
    return false;
  }
}

export function setPushOptedOut(v) {
  try {
    if (v) localStorage.setItem(OPT_OUT_KEY, "1");
    else localStorage.removeItem(OPT_OUT_KEY);
  } catch (e) {
    /* sem storage, fica o comportamento por defeito (ativo) */
  }
}

export function pushCapable() {
  return (
    typeof window !== "undefined" &&
    "serviceWorker" in navigator &&
    "PushManager" in window &&
    "Notification" in window
  );
}

export function isIOS() {
  if (typeof navigator === "undefined") return false;
  return (
    /iPad|iPhone|iPod/.test(navigator.userAgent) ||
    (navigator.userAgent.includes("Mac") && "ontouchend" in document)
  );
}

export function isStandalone() {
  if (typeof window === "undefined") return false;
  return (
    window.matchMedia("(display-mode: standalone)").matches ||
    (window.navigator && window.navigator.standalone === true)
  );
}

// No iPhone o push só existe com a app instalada no ecrã principal.
export function canPushHere() {
  return pushCapable() && (!isIOS() || isStandalone());
}

function withTimeout(p, ms, label) {
  return Promise.race([
    p,
    new Promise((_, reject) => setTimeout(() => reject(new Error(`timeout:${label}`)), ms)),
  ]);
}

function toBase64Url(buffer) {
  let bin = "";
  new Uint8Array(buffer).forEach((b) => (bin += String.fromCharCode(b)));
  return btoa(bin).replace(/\+/g, "-").replace(/\//g, "_").replace(/=+$/, "");
}

// Sem `options.applicationServerKey` (browsers antigos) não há como comparar:
// mantém-se a subscrição existente.
function subscriptionUsesKey(sub, publicKey) {
  const key = sub.options?.applicationServerKey;
  if (!key) return true;
  return toBase64Url(key) === publicKey.replace(/=+$/, "");
}

export class PushActivationError extends Error {
  constructor(code, message) {
    super(message || code);
    this.code = code;
  }
}

/**
 * Pede a permissão (se ainda não houver), garante o service worker, subscreve
 * e associa o endpoint à conta. Idempotente: o backend deduplica por endpoint.
 *
 * `Notification.requestPermission()` é a primeira chamada, sem `await` antes,
 * para continuar dentro do gesto que a disparou (exigência do Safari).
 */
export async function activatePush() {
  const perm = await Notification.requestPermission();
  if (perm === "denied") throw new PushActivationError("denied");
  if (perm !== "granted") throw new PushActivationError("dismissed");

  let registration = await navigator.serviceWorker.getRegistration();
  if (!registration) {
    try {
      registration = await navigator.serviceWorker.register("/sw.js");
    } catch (e) {
      throw new PushActivationError("sw_register");
    }
  }
  registration = await withTimeout(navigator.serviceWorker.ready, ACTIVATION_TIMEOUT_MS, "sw-ready");

  const { data } = await withTimeout(api.get("/push/vapid-public-key"), ACTIVATION_TIMEOUT_MS, "vapid");
  if (!data?.public_key) throw new PushActivationError("vapid");

  let sub = await withTimeout(registration.pushManager.getSubscription(), ACTIVATION_TIMEOUT_MS, "get-sub");
  // Subscrição criada com uma chave VAPID antiga (chaves rodadas no servidor):
  // o serviço de push rejeitaria os envios, por isso refaz-se com a chave actual.
  if (sub && !subscriptionUsesKey(sub, data.public_key)) {
    await withTimeout(sub.unsubscribe(), ACTIVATION_TIMEOUT_MS, "unsubscribe-stale");
    sub = null;
  }
  if (!sub) {
    sub = await withTimeout(subscribePush(data.public_key), ACTIVATION_TIMEOUT_MS, "subscribe");
  }

  const json = sub.toJSON();
  await withTimeout(
    api.post("/push/subscribe", {
      endpoint: json.endpoint,
      p256dh: json.keys.p256dh,
      auth: json.keys.auth,
      user_agent: navigator.userAgent,
    }),
    ACTIVATION_TIMEOUT_MS,
    "save",
  );
  setPushOptedOut(false);
  return sub;
}

// Uma tentativa automática por carregamento da app: se a pessoa fechar o
// pedido do sistema sem escolher, não se insiste a cada toque.
let autoAttempted = false;

export function startAutoPush() {
  if (autoAttempted || !canPushHere() || isPushOptedOut()) return () => {};
  const perm = Notification.permission;
  if (perm === "denied") return () => {};

  const run = () => {
    if (autoAttempted) return;
    autoAttempted = true;
    activatePush().catch(() => {});
  };
  if (perm === "granted") {
    run();
    return () => {};
  }

  const events = ["pointerup", "keyup"];
  const onGesture = () => {
    events.forEach((e) => document.removeEventListener(e, onGesture, true));
    run();
  };
  events.forEach((e) => document.addEventListener(e, onGesture, true));
  return () => events.forEach((e) => document.removeEventListener(e, onGesture, true));
}

export function resetAutoPushForTests() {
  autoAttempted = false;
}
